import {useEffect} from 'react';
import useClientConfig from '../lib/useClientConfig';
import webIconUrl from '../lib/webIconUrl';
import {joinTitle, useCurrentPageTitle} from './PageTitle';

// The favicon index.html ships with, captured before AppBranding first changes it so resetting
// the Web Icon restores it without a reload.
let defaultFavicon: {href: string; type: string} | null = null;

/** The built-in favicon, for previews of what a tenant gets with no Web Icon uploaded. */
export const getDefaultFaviconHref = () =>
    defaultFavicon?.href ??
    document.querySelector<HTMLLinkElement>('link[rel="icon"]')?.href ??
    '';

/**
 * White-labels the browser tab from the Branding settings: the Short Name leads the title (and
 * is the whole title when no PageTitle is mounted), and the uploaded Web Icon becomes the
 * favicon. Renders outside the auth gate so the login page is branded too.
 */
const AppBranding = () => {
    const {appShortName, webIconVersion, isLoading, error} = useClientConfig();
    const pageTitle = useCurrentPageTitle();

    useEffect(() => {
        document.title = joinTitle(appShortName, pageTitle);
    }, [appShortName, pageTitle]);

    useEffect(() => {
        // Keep the index.html icon until config arrives, so a failed fetch never leaves the
        // tab without one.
        if (isLoading || error) {
            return;
        }

        // Retarget the existing <link> rather than adding a second rel="icon", which
        // browsers resolve inconsistently.
        const link =
            document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (!link) {
            return;
        }
        defaultFavicon ??= {href: link.href, type: link.type};

        if (webIconVersion) {
            link.type = 'image/png';
            link.href = webIconUrl(webIconVersion);
        } else {
            link.type = defaultFavicon.type;
            link.href = defaultFavicon.href;
        }
    }, [isLoading, error, webIconVersion]);

    return null;
};

export default AppBranding;
